/**
 * shadcn-style Dialog wrapper around Radix Dialog. Inherits Radix's
 * focus-trap, ESC handler, scroll-lock, and return-focus on close —
 * which satisfies Q48 baseline a11y "for free" (research-B §3.3).
 */
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-[rgba(20,24,31,0.40)] backdrop-blur-[2px]',
      'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 flex w-[calc(100%-40px)] max-w-[520px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-line bg-card p-5 shadow-3 duration-base',
        // Потолок высоты по экрану. Окно центрировано
        // (`top-1/2 -translate-y-1/2`), поэтому без потолка длинное
        // содержимое растит его в обе стороны от центра, и подвал уходит
        // вниз за край экрана; прокручивается при этом страница под окном,
        // а не само окно. Тот же приём, что в `alert-dialog.tsx`, но
        // прокрутку здесь несёт ВНУТРЕННЯЯ область, а не сам контейнер —
        // иначе крестик `absolute` уезжает вместе с содержимым, и человек
        // остаётся без способа закрыть окно.
        'max-h-[calc(100dvh-40px)]',
        'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-out',
        className,
      )}
      {...props}
    >
      {/*
        Область прокрутки. Внешняя коробка — флекс-колонка без прокрутки,
        поэтому крестик, позиционированный от неё, закреплён.

        `min-h-0` — страховка, а НЕ несущий класс: у флекс-элемента с
        `overflow` не `visible` автоматический минимальный размер и так
        равен нулю, снятие класса не меняет геометрию ни на точку
        (проверено мутацией — спека остаётся зелёной). Оставлен на случай,
        если прокрутка отсюда уедет: тогда он снова станет несущим. Назван
        страховкой честно, поэтому и оставлен — в отличие от `min-w-0` на
        подвале, который выдавал себя за рубеж и был убран.

        Явная колонка `minmax(0, 1fr)` — разбор в `alert-dialog.tsx`:
        неявная колонка `auto` берёт минимум из min-content содержимого,
        поэтому одно длинное слово без пробелов растягивало сетку на тысячи
        пикселей и уносило подвал с кнопками за правый край окна. Это
        ЕДИНСТВЕННЫЙ держатель границы: `min-w-0` с шапки и подвала снят —
        его снятие не заметил ни один из 99 случаев. Снятие самой колонки
        роняет 6 случаев блока «тело окна».

        `[overflow-wrap:anywhere]` стоит здесь, а не на заголовке с
        описанием: свойство наследуемое, и на контейнере оно накрывает
        ЛЮБОЙ узел тела, который положит вызывающий код. Пока правило
        висело на двух узлах, тело окна не было защищено вовсе.

        `p-1` — запас под кольцо фокуса. `overflow-y-auto` вынуждает
        `overflow-x` вычислиться в `auto`, область становится контейнером
        прокрутки и ОБРЕЗАЕТ всё, что выходит за её границы. Полноширинные
        поле и кнопка лежат ровно по этим границам, и их кольцо
        (`ring-[3px]` у Input, `ring-2 ring-offset-1` у Button) срезалось
        слева, справа и снизу: запас был РОВНО ноль.

        `-m-1` — ОТДЕЛЬНОЕ свойство, а не половина предыдущего: оно
        возвращает содержимое на прежнее место, чтобы отступ окна остался
        ровно `p-5`, а не 24 точки. Кольца не касается вовсе, поэтому у
        него свой случай — «отступ окна остаётся 20 точек».
      */}
      <div className="-m-1 grid min-h-0 grid-cols-[minmax(0,1fr)] gap-3 overflow-y-auto overscroll-contain p-1 [overflow-wrap:anywhere]">
        {children}
      </div>
      <DialogPrimitive.Close
        // Здесь ДВА разных свойства с разными предметами, и путать их не
        // надо — один раз я их спутал и снял второе как «инертное».
        //
        // `bg-card` — от ПРОСВЕЧИВАНИЯ: содержимое прокручивается под
        // крестиком, и без непрозрачной подложки текст виден сквозь него.
        // Следствие визуальное, геометрией не ловится; случая на него нет
        // намеренно, и это названо в шапке спеки.
        //
        // `z-10` — от ПЕРЕКРЫТИЯ СЛОЕМ. `absolute` делает крестик
        // позиционированным, но с `z-index: auto`, поэтому любой узел
        // области прокрутки, который создаёт собственный слой (`sticky`,
        // `relative` + `z-index`, всплывашка), красит ПОВЕРХ него. Порядок
        // в разметке спасает только от содержимого БЕЗ слоя — на таком я и
        // проверял, когда ошибочно счёл класс инертным: проба не могла
        // увидеть разницу. Замер с подставленным `sticky z-index:1`: без
        // `z-10` в центре крестика лежит подставленный узел, с ним —
        // крестик. Подтверждает случай «крестик поверх слоя».
        className="absolute right-3 top-3 z-10 rounded-sm bg-card text-muted opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Закрыть"
      >
        <X className="size-4" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

// `pr-4` — место под крестик. Он висит на `right-3`, то есть в 12 точках от
// края padding-бокса, при своей ширине 16 и рамке окна в 1 — итого его левый
// край на 8 точек ЗАХОДИТ в область содержимого (замерено: край содержимого
// 839, левый край крестика 831). Без отступа последний знак первой строки
// заголовка заезжает под его непрозрачную подложку.
//
// Глубину пересечения знака с крестиком приводить числом бессмысленно: она
// зависит от содержимого полигона. На плотном тексте, которым гоняется
// случай, тест печатает 1.8 x 5.0 точек; на прежней заготовке с обычными
// словами было 0.3 x 5.0, а со словом без пробелов пересечения не возникало
// вовсе — строка обрывалась раньше. Важно не число, а что запаса не было.
//
// Крестик у `Dialog` есть всегда, поэтому отступ безусловный. Снятие роняет
// случай «заголовок не заезжает под крестик».
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 pr-4 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

// Подписи кнопок; разбор в `alert-dialog.tsx`. Правило взято ПОТОМКОМ: в
// живом коде кнопки бывают внуками подвала (обёртка внутри), прямого
// селектора не хватало. Соседние `min-w-0` убраны как ненаблюдаемые.
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
      '[&_a]:shrink [&_a]:whitespace-normal [&_button]:shrink [&_button]:whitespace-normal',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    // Правило переноса намеренно НЕ дублируется здесь: оно стоит один раз
    // на области прокрутки в `DialogContent` и наследуется.
    className={cn('text-base font-semibold tracking-tight text-ink', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-xs text-muted', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
